<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="row form-group">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('studentScoringEntry.advisor')
          }}</label>
          <select class="form-control" v-model="model.advisor_id">
            <option :value="1">{{ $t('studentscoringentry.teacher') }}</option>
            <option :value="2">
              {{ $t('studentscoringentry.management') }}
            </option>
            <option :value="3">{{ $t('studentscoringentry.parent') }}</option>
          </select>
          <div class="validate" v-if="errors.has('advisor_id')">
            {{ errors.first('advisor_id') }}
          </div>
        </div>
      </div>
      <div class="row form-group">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('studentScoringEntry.comment')
          }}</label>
          <VueEditor
            ref="editor"
            v-model="model.comments"
            :editor-toolbar="customToolbar"
            useCustomImageHandler
          />
          <p :class="{ over: isOver() }">
            {{
              isOver()
                ? `You are ${textLength -
                    maxCharacters} characters over the limit.`
                : `You have ${charactersRemaining} characters remaining.`
            }}
          </p>
          <div class="validate" v-if="errors.has('comments')">
            {{ errors.first('comments') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 text-right">
          <ts-button class="mr-2" @click="onCloseModal">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            v-if="model.sc_stu_com_id == 0"
            color="primary"
            :waiting="waiting"
            @click.prevent="onSaveComment"
            >{{ $t('save') }}</ts-button
          >
          <ts-button
            v-if="model.sc_stu_com_id != 0"
            color="primary"
            :waiting="waiting"
            @click.prevent="onUpdate"
            >{{ $t('update') }}</ts-button
          >
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { VueEditor } from 'vue2-editor'

export default {
  name: 'advisorComment',
  props: ['value'],
  components: { VueEditor },
  data() {
    return {
      loading: false,
      waiting: false,
      errors: new Errors(),
      quill: null,
      model: {
        sc_stu_com_id: 0,
        advisor_id: null,
        comments: null,
        student_id: null,
        class_id: null,
        st_class_id: null,
        scoring_cycle_id: null,
        suggesstion: null,
        cycle_value: null
      },
      customToolbar: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }]
      ],
      maxCharacters: 1500
    }
  },
  computed: {
    textLength() {
      if (this.quill) {
        return Math.max(this.quill.getLength() - 1, 0)
      }
      if (!this.model.comments) return 0
      return this.model.comments.replace(/<[^>]*>/g, '').length
    },
    charactersRemaining() {
      return Math.max(this.maxCharacters - this.textLength, 0)
    }
  },
  created() {
    this.model.student_id = this.value.student_id
    this.model.class_id = this.value.class_id
    this.model.st_class_id = this.value.st_class_id
    this.model.scoring_cycle_id = this.value.scoring_cycle_id
    this.model.cycle_value = this.value.cycle_value
    this.fetchComment()
  },
  mounted() {
    this.$nextTick(() => {
      if (!this.$refs.editor) return
      this.quill = this.$refs.editor.quill
    })
  },
  methods: {
    isOver() {
      return this.textLength > this.maxCharacters
    },

    onSaveComment() {
      if (this.isOver()) {
        this.$toasted.error(
          `Comment exceeds the ${this.maxCharacters} character limit by ${this
            .textLength -
            this
              .maxCharacters} characters. Please shorten your comment before saving.`
        )
        return
      }
      this.waiting = true
      this.$store
        .dispatch('studentScoringEntry/saveComment', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.$emit('closeModal')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },

    onUpdate() {
      if (this.isOver()) {
        this.$toasted.error(
          `Comment exceeds the ${this.maxCharacters} character limit by ${this
            .textLength -
            this
              .maxCharacters} characters. Please shorten your comment before saving.`
        )
        return
      }
      this.waiting = true
      this.$store
        .dispatch('studentScoringEntry/updateComment', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.$emit('closeModal')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },

    fetchComment() {
      this.loading = true
      this.$store
        .dispatch('studentScoringEntry/findComment', {
          params: {
            student_id: this.value.student_id,
            st_class_id: this.value.st_class_id,
            scoring_cycle_id: this.value.scoring_cycle_id,
            cycle_value: this.value.cycle_value
          }
        })
        .then(response => {
          if (response.data) {
            this.model.advisor_id = response.data.advisor_id
            this.model.comments = response.data.comments
            this.model.sc_stu_com_id = response.data.sc_stu_com_id
            this.model.suggesstion = response.data.suggesstion
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },

    onCloseModal() {
      this.clearInput()
      this.$emit('closeModal')
    },

    clearInput() {
      this.model.sc_stu_com_id = 0
      this.model.advisor_id = null
      this.model.comments = null
      this.model.student_id = null
      this.model.class_id = null
      this.model.st_class_id = null
      this.model.scoring_cycle_id = null
      this.model.suggesstion = null
      this.model.cycle_value = null
    }
  }
}
</script>

<style scoped>
.over {
  color: #dc3545 !important;
}
</style>
